/**
 * Authentification du dashboard — contrat partagé front / back.
 *
 * La connexion se fait en plusieurs pas. Chaque réponse de l'API dit quel est
 * le suivant, et porte un `ticket` (JWT court, 10 minutes) qui prouve que les
 * pas précédents ont été franchis. Le jeton de session n'est délivré qu'au
 * dernier pas.
 *
 *   mot de passe ──► (à renouveler ?) ──► (second facteur) ──► session
 *
 * Politique (docs/securite/politique-securite.md) : mot de passe de 12
 * caractères dont un spécial, renouvelé tous les 365 jours ; code TOTP à
 * chaque connexion, sauf sur un appareil de confiance (30 jours).
 */

import type { AdminSummary } from './stats';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const PASSWORD_MAX_AGE_DAYS = 365;
export const TRUSTED_DEVICE_DAYS = 30;

/**
 * Règles du mot de passe, même code des deux côtés : le formulaire prévient
 * avant d'envoyer, l'API refuse quoi qu'il arrive.
 *
 * Renvoie la liste des règles NON respectées, vide si tout va bien.
 */
export function passwordPolicyIssues(password: string): string[] {
  const issues: string[] = [];
  if (password.length < PASSWORD_MIN_LENGTH) issues.push(`au moins ${PASSWORD_MIN_LENGTH} caractères`);
  if (password.length > PASSWORD_MAX_LENGTH) issues.push(`au plus ${PASSWORD_MAX_LENGTH} caractères`);
  if (!/[a-z]/.test(password)) issues.push('une minuscule');
  if (!/[A-Z]/.test(password)) issues.push('une majuscule');
  if (!/[0-9]/.test(password)) issues.push('un chiffre');
  // « Spécial » : tout ce qui n'est ni lettre ni chiffre, espace compris.
  if (!/[^A-Za-z0-9]/.test(password)) issues.push('un caractère spécial (ponctuation, symbole…)');
  return issues;
}

/** Rappel des règles, pour l'aide du formulaire. */
export const PASSWORD_POLICY_TEXT =
  `${PASSWORD_MIN_LENGTH} caractères minimum, avec une majuscule, une minuscule, un chiffre et un caractère spécial.`;

// -----------------------------------------------------------------------------
// Rôles
//
//   admin  tout le dashboard, lecture et écriture.
//   user   lecture seule de quatre pages : Résumé, Shopify, Ads, Analytics
//          (plus sa page Compte). Pas de saisie, pas d'assistant.
//
// L'API applique la liste blanche ci-dessous à chaque requête ; le front s'en
// sert pour la barre latérale et les routes, ce qui n'est que cosmétique.
// -----------------------------------------------------------------------------

export type AdminRole = 'admin' | 'user';

export const ADMIN_ROLES: readonly AdminRole[] = ['admin', 'user'];

/** Pages du front ouvertes au rôle `user`. */
export const USER_ROLE_PAGES: readonly string[] = ['/', '/shopify', '/ads', '/analytics', '/compte'];

/**
 * Routes de l'API ouvertes au rôle `user`, sous la forme « MÉTHODE motif »
 * (motif Fastify, préfixe compris).
 *
 * Le Résumé agrège presque toutes les sources (abonnements, Amazon, BtoB,
 * branding, charges, réglages) : ces lectures-là sont donc ouvertes, mais
 * aucune écriture, et ni l'usage de l'app, ni le SAV, ni l'assistant.
 */
export const USER_ROLE_API: ReadonlySet<string> = new Set([
  // Compte
  'GET /api/auth/me',
  'POST /api/auth/devices/forget',
  'POST /api/auth/sessions/revoke',
  // Résumé, Shopify, Ads
  'GET /api/sales',
  'GET /api/sales/subscriptions',
  'GET /api/sales/ads',
  'GET /api/sales/ads/daily',
  'GET /api/sales/amazon',
  'GET /api/sales/amazon-ads',
  'GET /api/sales/google-ads',
  'GET /api/btob/stats',
  'GET /api/branding/stats',
  'GET /api/charges/stats',
  'GET /api/settings',
  'GET /api/settings/deductions',
  // Marqueurs affichés sur les graphiques (lecture)
  'GET /api/markers',
  // Analytics (GA4) et sessions GA4 du Résumé
  'GET /api/traffic/stats',
  'GET /api/traffic/ga4',
]);

/** Le rôle peut-il appeler cette route ? `routeUrl` est le motif Fastify. */
export function roleAllowsApi(role: AdminRole, method: string, routeUrl: string): boolean {
  if (role === 'admin') return true;
  // `app.get('/')` sous le préfixe `/api/sales` répond sur `/api/sales` ET
  // `/api/sales/` : la barre finale ne compte pas.
  const url = routeUrl.length > 1 ? routeUrl.replace(/\/+$/, '') : routeUrl;
  return USER_ROLE_API.has(`${method.toUpperCase()} ${url}`);
}

/** Le rôle peut-il ouvrir cette page du front ? */
export function roleAllowsPage(role: AdminRole, pathname: string): boolean {
  return role === 'admin' || USER_ROLE_PAGES.includes(pathname);
}

// -----------------------------------------------------------------------------
// POST /api/auth/login
// -----------------------------------------------------------------------------

export interface LoginBody {
  email: string;
  password: string;
  /** Jeton d'appareil de confiance reçu à une connexion précédente, s'il y en a un. */
  deviceToken?: string;
}

/** Connexion terminée : jeton de session. */
export interface LoginDone {
  step: 'ok';
  token: string;
  admin: AdminSummary;
  /** Présent quand l'appareil vient d'être marqué de confiance. À conserver. */
  deviceToken?: string;
}

/** Le mot de passe doit être changé avant d'aller plus loin. */
export interface LoginPasswordChange {
  step: 'password_change';
  ticket: string;
  /** Pourquoi : trop vieux, ou en deçà de la politique actuelle. */
  reason: 'expired' | 'policy';
}

/** Premier passage : le second facteur doit être enrôlé. */
export interface LoginMfaSetup {
  step: 'mfa_setup';
  ticket: string;
  /** Secret en base32, à saisir à la main si le QR code ne peut pas être scanné. */
  secret: string;
  /** URL `otpauth://` à encoder en QR code. */
  otpauthUrl: string;
}

/** Second facteur enrôlé : un code est attendu. */
export interface LoginMfa {
  step: 'mfa';
  ticket: string;
}

export type LoginStep = LoginDone | LoginPasswordChange | LoginMfaSetup | LoginMfa;

// -----------------------------------------------------------------------------
// POST /api/auth/password — avec un ticket (changement imposé) ou un jeton de
// session + mot de passe actuel (changement volontaire).
// -----------------------------------------------------------------------------

export interface PasswordChangeBody {
  ticket?: string;
  currentPassword?: string;
  newPassword: string;
}

// -----------------------------------------------------------------------------
// POST /api/auth/mfa/verify
// -----------------------------------------------------------------------------

export interface MfaVerifyBody {
  ticket: string;
  /** Six chiffres. */
  code: string;
  /** Dispenser cet appareil du code pendant TRUSTED_DEVICE_DAYS jours. */
  rememberDevice?: boolean;
}

// -----------------------------------------------------------------------------
// GET /api/auth/me — état du compte connecté (page Compte)
// -----------------------------------------------------------------------------

export interface AccountResponse {
  admin: AdminSummary;
  passwordChangedAt: string;
  /** Jour où la connexion imposera un nouveau mot de passe. */
  passwordExpiresAt: string;
  mfaEnabledAt: string | null;
  trustedDevices: number;
}
